import { useMemo, useState } from "react";
import { Star } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";

type RawScores = {
  appropriatenessScore?: number | null;
  qualityScore?: number | null;
  costEfficiencyScore?: number | null;
  effectivenessScore?: number | null;
};

type Metric = {
  label: string;
  value: number | null;
};

type ProviderMetricsProps = {
  scores: RawScores;
};

const labelMap: Record<keyof RawScores, string> = {
  appropriatenessScore: "Appropriateness",
  qualityScore: "Quality",
  costEfficiencyScore: "Cost Efficiency",
  effectivenessScore: "Effectiveness",
};

function StarRating({ value }: { value: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => {
        const fillPercent = Math.max(0, Math.min(1, value - i)) * 100;
        return (
          <div key={i} className="relative size-5">
            {/* Empty base star */}
            <Star className="size-5 text-gray-300" fill="currentColor" />
            {/* Filled portion clipped based on decimal */}
            {fillPercent > 0 && (
              <div
                className="absolute left-0 top-0 h-full overflow-hidden"
                style={{ width: `${fillPercent}%` }}
              >
                <Star className="size-5 text-yellow-500" fill="currentColor" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ProviderMetrics({ scores }: ProviderMetricsProps) {
  const [open, setOpen] = useState(false);

  const ratingArray: Metric[] = useMemo(() => {
    return Object.entries(scores).map(([key, value]) => ({
      label: labelMap[key as keyof RawScores],
      value: value ?? null,
    }));
  }, [scores]);

  const validRatings = ratingArray.filter((r) => r.value !== null);

  const overallRating = useMemo(() => {
    if (validRatings.length === 0) return 0;
    const avg =
      validRatings.reduce((sum, r) => sum + (r.value ?? 0), 0) /
      validRatings.length;
    return Math.round(avg * 2) / 2;
  }, [validRatings]);

  return (
    <div>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <button className="flex cursor-pointer items-center gap-2">
            <span className="text-medium font-medium text-gray-700">
              Overall Rating
            </span>
            <StarRating value={overallRating} />
          </button>
        </Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/75" />
          <Dialog.Content
            aria-describedby={undefined}
            className="fixed left-1/2 top-1/2 z-50 w-[90vw] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-lg"
          >
            <div className="space-y-3">
              {validRatings.map((rating, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-medium font-medium text-gray-700">
                    {rating.label}
                  </span>
                  <StarRating value={rating.value ?? 0} />
                </div>
              ))}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
